/**
 * Reactive-variable store for the pipeline Rules & automation panel (SE-5506).
 *
 * Holds the panel's open/closed state and the board it is scoped to. Uses Apollo
 * `makeVar` to match the frontend-v2 store convention (mirrors
 * `reviewQueueDrawerStore`), so any toolbar surface can open the panel without
 * prop-drilling a callback.
 * @module features/player-kanban/stores/rulesPanelStore
 */

import { makeVar } from "@apollo/client";

/** Serializable state of the Rules panel. */
export interface RulesPanelState {
  /** Whether the panel is open. */
  readonly open: boolean;
  /**
   * Pipeline the panel is scoped to. Rules are per-board, so a board id is
   * required to fetch/create; null means the panel is closed / unscoped.
   */
  readonly kanbanBoardId: string | null;
}

/** Initial (closed) state. */
const INITIAL_STATE: RulesPanelState = {
  open: false,
  kanbanBoardId: null,
};

/**
 * Apollo reactive variable holding the Rules-panel state.
 * @remarks Subscribe via `useReactiveVar(rulesPanelVar)` (or the
 * `useRulesPanelState` hook) and update through the helper below.
 */
export const rulesPanelVar = makeVar<RulesPanelState>(INITIAL_STATE);

/** Reset the panel to its initial closed state. */
export function resetRulesPanel(): void {
  rulesPanelVar(INITIAL_STATE);
}
